import { useQuery } from '@tanstack/react-query';
import type { ApiClient } from '../../api/client';
import type { BookingStatusResponse, StatusView } from '../../types';
import { formatSessionDate } from '../../lib/format';
import { Spinner } from '../Spinner';
import styles from '../BookingWidget.module.css';
import { bookingWidgetStyles } from '../bookingWidgetStyles';

interface StatusStepProps {
  api: ApiClient;
  bookingId: string;
  accessToken: string;
  initialView: StatusView;
  initialData: BookingStatusResponse | null;
}

function statusCopy(view: StatusView): { title: string; text: string; icon: string } {
  switch (view) {
    case 'confirmed':
      return {
        icon: '✓',
        title: 'Buchung bestätigt',
        text: 'Vielen Dank! Du erhältst in Kürze eine Bestätigung per E-Mail.',
      };
    case 'processing':
      return {
        icon: '…',
        title: 'Zahlung wird verarbeitet',
        text: 'Wir prüfen deine Zahlung. Das dauert meist nur wenige Sekunden.',
      };
    case 'failed':
      return {
        icon: '!',
        title: 'Zahlung fehlgeschlagen',
        text: 'Die Zahlung konnte nicht abgeschlossen werden. Du kannst es erneut versuchen.',
      };
    case 'expired':
      return {
        icon: '⏱',
        title: 'Reservierung abgelaufen',
        text: 'Deine Reservierung ist leider abgelaufen. Bitte starte eine neue Buchung.',
      };
  }
}

const STATUS_CLASS: Record<StatusView, string> = {
  confirmed: bookingWidgetStyles.statusConfirmed,
  processing: bookingWidgetStyles.statusProcessing,
  failed: bookingWidgetStyles.statusFailed,
  expired: bookingWidgetStyles.statusFailed,
};

function statusClass(view: StatusView): string {
  return STATUS_CLASS[view];
}

function resolveView(data: BookingStatusResponse): StatusView {
  if (data.bookingStatus === 'confirmed') {
    return 'confirmed';
  }
  if (data.bookingStatus === 'expired') {
    return 'expired';
  }
  if (
    data.bookingStatus === 'payment_failed' ||
    data.paymentStatus === 'failed' ||
    data.paymentStatus === 'canceled' ||
    data.paymentStatus === 'expired'
  ) {
    return 'failed';
  }
  return 'processing';
}

export function StatusStep({
  api,
  bookingId,
  accessToken,
  initialView,
  initialData,
}: StatusStepProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['booking-status', bookingId],
    queryFn: () => api.getBookingStatus(bookingId, accessToken),
    initialData: initialData ?? undefined,
    refetchInterval: (query) => {
      const current = query.state.data;
      if (!current) {
        return 2500;
      }
      return resolveView(current) === 'processing' ? 2500 : false;
    },
    enabled: Boolean(bookingId && accessToken),
  });

  const view = data ? resolveView(data) : initialView;
  const copy = statusCopy(view);

  if (isLoading && !data) {
    return <Spinner centered large />;
  }

  return (
    <div className={styles.statusPanel}>
      <div className={statusClass(view)} aria-hidden="true">
        {view === 'processing' ? <Spinner /> : copy.icon}
      </div>
      <h3 className={styles.statusTitle}>{copy.title}</h3>
      <p className={styles.statusText}>{copy.text}</p>

      {data?.reference ? (
        <div className={styles.reference}>
          Referenz: <strong>{data.reference}</strong>
        </div>
      ) : null}

      {data?.classTitle && data.startsAt ? (
        <div className={styles.reference}>
          <strong>{data.classTitle}</strong>
          <br />
          {formatSessionDate(data.startsAt)}
          <br />
          {new Intl.DateTimeFormat('de-CH', {
            hour: '2-digit',
            minute: '2-digit',
          }).format(new Date(data.startsAt))}{' '}
          Uhr
        </div>
      ) : null}

      {data?.invoiceNumber ? (
        <div className={styles.reference}>
          Rechnung: <strong>{data.invoiceNumber}</strong>
        </div>
      ) : null}

      {data?.creditsRemaining != null && data.creditsRemaining > 0 ? (
        <div className={styles.reference}>
          Noch <strong>{data.creditsRemaining}</strong>{' '}
          {data.creditsRemaining === 1 ? 'Credit' : 'Credits'} übrig – du kannst sie
          später unter «Guthaben einlösen» verwenden.
        </div>
      ) : null}
    </div>
  );
}
